import React from 'react';
import { AlertCircle } from 'lucide-react';

interface ErrorAlertProps {
    readonly message: string | any; // Allow any type but we'll convert to string
    readonly onDismiss?: () => void;
    readonly className?: string;
    readonly showIcon?: boolean;
}

// Helper function to safely convert any value to a string
const getMessageString = (message: any): string => {
    if (typeof message === 'string') {
        return message;
    }
    
    if (message && typeof message === 'object') {
        // Handle API error objects
        if (message.message && typeof message.message === 'string') {
            return message.message;
        }
        
        // Handle other error formats
        if (message.statusText && typeof message.statusText === 'string') {
            return message.statusText;
        }
        
        // Log warning for debugging
        console.warn('⚠️ ErrorAlert received object instead of string:', message);
        
        // Fallback for unknown error objects
        return message.toString ? message.toString() : 'An error occurred';
    }
    
    if (message === null || message === undefined) {
        return 'An error occurred';
    }
    
    // Convert any other type to string
    return String(message);
};

export default function ErrorAlert({ 
    message, 
    onDismiss, 
    className = "", 
    showIcon = true 
}: ErrorAlertProps) {
    // Ensure message is always a string
    const messageString = getMessageString(message);
    
    return (
        <div className={`mb-4 p-3 bg-red-100 border border-red-400 text-red-700 rounded-lg flex items-center gap-2 mx-auto ${className}`}>
            {showIcon && <AlertCircle size={18} />}
            <span className="text-sm flex-grow">{messageString}</span>
            {onDismiss && (
                <button 
                    onClick={onDismiss}
                    className="ml-auto text-red-700 hover:text-red-900 text-lg leading-none"
                    aria-label="Dismiss error"
                >
                    ×
                </button>
            )}
        </div>
    );
} 